import * as React from 'react';
import { View, Text, Flex } from '@adobe/react-spectrum';
import { IColumnItem } from '../ColumnsList';
import { FilterControlRenderer } from './components/FilterControlRenderer';
import styles from './FilterSetupPlaceholder.module.css';
import { TrashIcon } from '../../../Icons/IconsLib';
import { usePreloadAssets } from '../../../../../hooks/UsePreloadAssets';

export interface IFilterItem {
  name: string;
  type: string; // Filter operation type: "equals", "contains", "range", etc.
  value?: string;
  valueLabel?: string; // Display label for status/type filters (comma-separated labels matching value IDs)
  negation?: boolean;
  includeNull?: boolean;
  isCurrentUser?: boolean; // Flag to indicate current user selection
}

export interface IFilterControl {
  name: string; // Field name (itemname) used for API calls and as unique identifier
  label?: string; // Display label for the filter (falls back to name if not provided)
  dataType: string; // AOT field type: "text", "numeric", "date", "currency", etc.
  data: IColumnItem; // The selected column item
  filterInfo: IFilterItem[]; // Array of filter items with logical operations
}

export interface IStatusOption {
  id: string;
  label: string;
  color?: string;
}

export interface ITypeOption {
  id: string;
  label: string;
}

export interface IFilterSetupPlaceholderProps {
  filters: IFilterControl[];
  onFiltersChange: (filters: IFilterControl[]) => void;
  availableItems?: IColumnItem[];
  fpaTypeCategory?: string;
}

export function FilterSetupPlaceholder(props: IFilterSetupPlaceholderProps) {
  const { filters, onFiltersChange, fpaTypeCategory } = props;
  const {
    folderTypes: folderTypesState,
    projectTypes: projectTypesState,
    folderStatusMapping2: folderStatusState2,
    folderStatusMapping: folderStatusState,
    projectStatusMapping: projectStatusState,
    activityStatusMapping: activityStatusState,
    activityTypes: activityTypesState,
  } = usePreloadAssets();

  const mapStatusRecordToOptions = React.useCallback((record: Record<string, any> | undefined): IStatusOption[] => {
    if (!record) return [];
    return Object.entries(record).map(([id, value]) => ({
      id: String(id),
      label: value?.name ?? value?.status?.name ?? String(id),
      color: value?.status?.color ?? value?.color ?? value?.STATUS?.color ?? '#9E9E9E',
    }));
  }, []);

  const mapTypeRecordToOptions = React.useCallback((record: Record<string, any> | any[] | undefined): ITypeOption[] => {
    if (!record) return [];
    const values = Array.isArray(record) ? record : Object.values(record);

    return values
      .map((value) => {
        const rawId = value?.id ?? value?.TYPE?.id ?? value?.typeId ?? value?.TYPE_ID ?? value?.TypeId;
        const rawLabel =
          value?.name ??
          value?.TYPE?.name ??
          value?.typeName ??
          value?.TYPE_NAME ??
          value?.TypeName ??
          (rawId !== undefined ? String(rawId) : undefined);

        if (rawId === undefined && !rawLabel) {
          return null;
        }

        return {
          id: String(rawId ?? rawLabel),
          label: rawLabel ? String(rawLabel) : String(rawId),
        };
      })
      .filter((option): option is ITypeOption => Boolean(option));
  }, []);

  const folderStatusOptions = React.useMemo(
    () => mapStatusRecordToOptions(folderStatusState2 || folderStatusState),
    [folderStatusState, folderStatusState2, mapStatusRecordToOptions]
  );
  const projectStatusOptions = React.useMemo(
    () => mapStatusRecordToOptions(projectStatusState),
    [projectStatusState, mapStatusRecordToOptions]
  );
  const activityStatusOptions = React.useMemo(
    () => mapStatusRecordToOptions(activityStatusState),
    [activityStatusState, mapStatusRecordToOptions]
  );

  const folderTypeOptions = React.useMemo(
    () => mapTypeRecordToOptions(folderTypesState),
    [folderTypesState, mapTypeRecordToOptions]
  );
  const projectTypeOptions = React.useMemo(
    () => mapTypeRecordToOptions(projectTypesState),
    [projectTypesState, mapTypeRecordToOptions]
  );
  const activityTypeOptions = React.useMemo(
    () =>{ 
      var options = mapTypeRecordToOptions(activityTypesState)
      return options;
    },
    [activityTypesState, mapTypeRecordToOptions]
  ); 

  const handleDelete = React.useCallback((filterItemname: string) => {
    const updatedFilters = filters.filter(filter => filter.data.itemname !== filterItemname);
    onFiltersChange(updatedFilters);
  }, [filters, onFiltersChange]);

  const handleFilterChange = React.useCallback((updatedFilter: IFilterControl) => {
    const updatedFilters = filters.map(f => f.data.itemname === updatedFilter.data.itemname ? updatedFilter : f);
    onFiltersChange(updatedFilters);
  }, [filters, onFiltersChange]);

  if (filters.length === 0) {
    return (
      <View UNSAFE_className={styles.filterSetupPlaceholder}>
        <Text>List of default filters. You can add more filters.</Text>
      </View>
    );
  }

  return (
    <View UNSAFE_className={styles.filterSetupContainer}>
      <Text UNSAFE_className={styles.headerText}>
        List of default filters. You can add more filters.
      </Text>
      <Flex direction="column" gap="size-100" UNSAFE_className={styles.filtersList}>
        {filters.map((filter) => (
          <View key={filter.data.itemname} UNSAFE_className={styles.filterControl}>
            <Flex 
              direction="row" 
              alignItems="center" 
              justifyContent="space-between"
              UNSAFE_className={styles.filterControlHeader}
            >
              <Text UNSAFE_className={styles.filterName}>{filter.name}</Text>
              <button
                type="button"
                className={styles.deleteButton}
                onClick={() => handleDelete(filter.data.itemname)}
                aria-label={`Delete ${filter.name} filter`}
              >
                <TrashIcon size={24} />
              </button>
            </Flex>
            <div className={styles.filterControlContent}>
              <FilterControlRenderer
                filter={filter}
                onFilterChange={handleFilterChange}
                statusOptions={{
                  folder: folderStatusOptions,
                  project: projectStatusOptions,
                  activity: activityStatusOptions,
                }}
                typeOptions={{
                  folder: folderTypeOptions,
                  project: projectTypeOptions,
                  activity: activityTypeOptions,
                }}
                fpaTypeCategory={fpaTypeCategory}
              />
            </div>
          </View>
        ))}
      </Flex>
    </View>
  );
}

